import { baseApi } from "@/shared/api";
import { createFullySignedPatch } from "@/shared/utils";
import type { SigningDocument, SigningActionResponse } from "@/shared/types";
import { decrementPendingActionsCount } from "@/features/auth";
import { pendingActionsApi } from "@/features/self/pending/api/pendingActions";
import { authApi } from "@/features/auth/api";
import type { SignDocumentDetails } from "@/features/self/pending/types/pending.types";

interface CounterSignResponse {
  signing_document_id: string;
  sign_id: number;
}

interface OrgSignResponse {
  document_id: string;
  status: number;
  status_display: string;
}

export const signingDocumentsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getSigningDocument: builder.query<SigningDocument, string>({
      query: (documentId) => ({
        url: `/api/signing-documents/${documentId}/`,
        method: "GET",
        credentials: "include" as const,
      }),
      providesTags: (_result, _err, documentId) => [{ type: "SigningDocuments" as const, id: documentId }],
    }),
    counterSignDocument: builder.mutation<CounterSignResponse, { documentId: string; cms_signature: string }>({
      query: ({ documentId, cms_signature }) => ({
        url: `/api/signing-documents/${documentId}/counter-sign/`,
        method: "POST",
        body: { cms_signature },
        credentials: "include" as const,
      }),
      invalidatesTags: [
        { type: "Contracts" as const, id: "LIST" },
        { type: "LeaveApplications" as const, id: "LIST" },
        { type: "Amendments" as const, id: "LIST" },
        { type: "ResignationLetters" as const, id: "LIST" },
        { type: "CompletionActs" as const, id: "LIST" },
      ],
      async onQueryStarted({ documentId }, { dispatch, queryFulfilled }) {
        const pendingListPatch = dispatch(
          pendingActionsApi.util.updateQueryData("getPendingActions", undefined, (draft) => {
            const idx = draft.items.findIndex(
              (item) =>
                "document_id" in item.details &&
                (item.details as SignDocumentDetails).document_id === documentId,
            );
            if (idx !== -1) {
              draft.items.splice(idx, 1);
              draft.summary.total_count = Math.max(0, draft.summary.total_count - 1);
            }
          }),
        );

        const pendingCountPatch = dispatch(
          authApi.util.updateQueryData("getPendingCount", undefined, (draft) => {
            if (draft.count > 0) draft.count -= 1;
          }),
        );

        try {
          await queryFulfilled;
          dispatch(decrementPendingActionsCount());
          dispatch(
            signingDocumentsApi.util.updateQueryData("getSigningDocument", documentId, (draft) => {
              Object.assign(draft, createFullySignedPatch());
            }),
          );
        } catch {
          pendingListPatch.undo();
          pendingCountPatch.undo();
        }
      },
    }),
    orgSignDocument: builder.mutation<OrgSignResponse, { documentId: string; cms_signature: string }>({
      query: ({ documentId, cms_signature }) => ({
        url: `/api/signing-documents/${documentId}/org-sign/`,
        method: "POST",
        body: { cms_signature },
        credentials: "include" as const,
      }),
      invalidatesTags: [{ type: "Advances" as const, id: "LIST" }, "PendingActions"],
      async onQueryStarted({ documentId }, { dispatch, queryFulfilled }) {
        const pendingListPatch = dispatch(
          pendingActionsApi.util.updateQueryData("getPendingActions", undefined, (draft) => {
            const idx = draft.items.findIndex(
              (item) =>
                "document_id" in item.details &&
                (item.details as SignDocumentDetails).document_id === documentId,
            );
            if (idx !== -1) {
              draft.items.splice(idx, 1);
              draft.summary.total_count = Math.max(0, draft.summary.total_count - 1);
            }
          }),
        );

        const pendingCountPatch = dispatch(
          authApi.util.updateQueryData("getPendingCount", undefined, (draft) => {
            if (draft.count > 0) draft.count -= 1;
          }),
        );

        try {
          await queryFulfilled;
          dispatch(decrementPendingActionsCount());
        } catch {
          pendingListPatch.undo();
          pendingCountPatch.undo();
        }
      },
    }),
    revokeSigningDocument: builder.mutation<SigningActionResponse, string>({
      query: (documentId) => ({
        url: `/api/signing-documents/${documentId}/revoke/`,
        method: "POST",
        credentials: "include" as const,
      }),
      invalidatesTags: [
        "PendingActions",
        { type: "Contracts" as const, id: "LIST" },
        { type: "LeaveApplications" as const, id: "LIST" },
        { type: "Amendments" as const, id: "LIST" },
        { type: "ResignationLetters" as const, id: "LIST" },
        { type: "IncomeRecords" as const, id: "LIST" },
      ],
    }),
  }),
});

export const {
  useGetSigningDocumentQuery,
  useLazyGetSigningDocumentQuery,
  useCounterSignDocumentMutation,
  useOrgSignDocumentMutation,
  useRevokeSigningDocumentMutation,
} = signingDocumentsApi;
